/**
 * Calcular frete no PDP (Brasil / CEP) — mesma lógica do tema Pé na Trilha.
 * Fluxo: backup do carrinho → clear → add da variante do formulário → shipping_rates → restore.
 * O markup e o HTML dos resultados continuam os de snippets/shipping-estimator.liquid + shipping-estimator.css.
 */
(() => {
  const UF_TO_PROVINCE = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins',
  };

  const MESSAGES = {
    invalidZip: 'Insira um CEP válido',
    noVariant: 'Não foi possível identificar o produto selecionado.',
    unavailable: 'Não existem formas de entrega disponíveis para o seu endereço ou o produto selecionado não possui estoque!',
    error: 'Não foi possível calcular o frete para seu endereço. Tente novamente mais tarde!',
  };

  const MONTHS = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];

  const JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  const cartUrl = () => window.theme?.routes?.cart_url || '/cart';
  const cartAddUrl = () => window.theme?.routes?.cart_add_url || '/cart/add';
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const formatarData = (dataStr) => {
    const [, mes, dia] = String(dataStr).split('-');
    return `${parseInt(dia, 10)} de ${MONTHS[parseInt(mes, 10) - 1]}`;
  };

  const gerarTextoFrete = (opcao) => {
    const range = Array.isArray(opcao.delivery_range) ? opcao.delivery_range : null;
    const dias = Array.isArray(opcao.delivery_days) ? opcao.delivery_days : null;
    const preco = parseFloat(opcao.price);

    let textoData = 'Seu pedido chegará até';
    if (range && range.length === 2 && range[0] !== range[1]) {
      textoData = `Seu pedido chegará entre os dias ${formatarData(range[0]).split(' de ')[0]} a ${formatarData(range[1])}`;
    } else if (range && range.length > 0) {
      textoData = `Seu pedido chegará até o dia ${formatarData(range[0])}`;
    }

    let textoDias = '';
    if (dias && dias.length > 0) {
      textoDias = dias[0] !== dias[1] && dias[1] != null ? `(${dias[0]} - ${dias[1]} dias úteis)` : `(${dias[0]} dias úteis)`;
    }

    const textoPreco = preco === 0 ? 'Frete Grátis' : `Frete R$ ${preco.toFixed(2).replace('.', ',')}`;

    return `${textoData} ${textoDias} - ${textoPreco}`;
  };

  const renderMessage = (results, message) => {
    results.innerHTML = `<ul><li>${escapeHtml(message)}</li></ul>`;
  };

  const renderRates = (results, shippingRates) => {
    if (!shippingRates?.length) {
      renderMessage(results, MESSAGES.unavailable);
      return;
    }

    const items = shippingRates
      .map(
        (rate) => `
          <div class="shipping-estimator-item">
            <div>
              <strong>${escapeHtml(rate.name || rate.presentment_name || '')}</strong>: ${escapeHtml(gerarTextoFrete(rate))}
            </div>
          </div>`
      )
      .join('');

    results.innerHTML = `
      <div class="shipping-estimator-wrapper">
        <div class="shipping-estimator-title"><strong>Opções de entrega</strong></div>
        ${items}
      </div>`;
  };

  const getProductItem = (formId) => {
    const form =
      document.getElementById(`product-form-${formId}`) || document.getElementById('product-form-installment');

    if (form) {
      const formData = new FormData(form);
      const variantId = formData.get('id') || form.querySelector('[name="id"]')?.value;

      if (variantId) {
        const item = { id: Number(variantId), quantity: Number(formData.get('quantity')) || 1 };
        const properties = {};

        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        if (Object.keys(properties).length) item.properties = properties;

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = sellingPlan;

        return item;
      }
    }

    const variants = window.currentProduct?.variants;
    if (!Array.isArray(variants) || !variants.length) return null;

    const variant = variants.find((v) => v.available) || variants[0];
    return { id: variant.id, quantity: 1 };
  };

  const resolveProvince = async (cep) => {
    try {
      const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${cep}`);
      if (!response.ok) return UF_TO_PROVINCE.SP;

      const data = await response.json();
      return UF_TO_PROVINCE[data.state] || data.state || UF_TO_PROVINCE.SP;
    } catch (_) {
      return UF_TO_PROVINCE.SP;
    }
  };

  const backupCart = async () => {
    const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart backup failed');

    const cart = await response.json();
    return (cart.items || []).map((item) => {
      const out = { id: item.variant_id, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length) out.properties = item.properties;
      if (item.selling_plan_allocation?.selling_plan?.id) out.selling_plan = item.selling_plan_allocation.selling_plan.id;
      return out;
    });
  };

  const clearCart = () => fetch(`${cartUrl()}/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });

  const addItems = async (items) => {
    const response = await fetch(`${cartAddUrl()}.js`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items }),
    });
    return response.ok;
  };

  const restoreCart = async (items) => {
    await clearCart();
    if (items.length) await addItems(items);

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
  };

  const pollAsyncRates = async (address) => {
    const body = new URLSearchParams();
    for (const [key, value] of Object.entries(address)) body.append(`shipping_address[${key}]`, value);

    const prepare = await fetch(`${cartUrl()}/prepare_shipping_rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    if (!prepare.ok) throw new Error('Prepare failed');

    for (let attempt = 0; attempt < 10; attempt++) {
      await wait(1000);
      const response = await fetch(`${cartUrl()}/async_shipping_rates`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (response.status !== 200) continue;

      const json = await response.json();
      if (json?.shipping_rates?.length) return json.shipping_rates;
    }
    throw new Error('Timeout polling');
  };

  const fetchRates = async (address) => {
    const url = new URL(`${cartUrl()}/shipping_rates.json`, window.location.origin);
    for (const [key, value] of Object.entries(address)) url.searchParams.set(`shipping_address[${key}]`, value);

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Sync rates failed');

      const json = await response.json();
      return json.shipping_rates || [];
    } catch (_) {
      return pollAsyncRates(address);
    }
  };

  const estimate = async (estimator) => {
    const button = estimator.querySelector('.shipping_estimator_submit');
    const results = estimator.querySelector('.shipping-estimator--results');
    const zipInput = estimator.querySelector('.shipping-estimator-zip');
    if (!button || !results || !zipInput || button.classList.contains('loading')) return;

    const cep = zipInput.value.replace(/\D/g, '');
    if (!/^[0-9]{8}$/.test(cep)) {
      renderMessage(results, MESSAGES.invalidZip);
      return;
    }

    const productItem = getProductItem(estimator.dataset.formId);
    if (!productItem) {
      renderMessage(results, MESSAGES.noVariant);
      return;
    }

    button.classList.add('loading');
    let backup = null;

    try {
      const [province, cartItems] = await Promise.all([resolveProvince(cep), backupCart()]);
      backup = cartItems;

      await clearCart();
      if (!(await addItems([productItem]))) {
        renderMessage(results, MESSAGES.unavailable);
        return;
      }

      renderRates(results, await fetchRates({ zip: cep, country: 'Brazil', province }));
    } catch (_) {
      renderMessage(results, MESSAGES.error);
    } finally {
      if (backup) {
        try {
          await restoreCart(backup);
        } catch (_) {
          renderMessage(results, MESSAGES.error);
        }
      }
      button.classList.remove('loading');
    }
  };

  const init = () => {
    for (const wrapper of document.querySelectorAll('.shipping_estimator_pdp_wrapper')) {
      if (wrapper.dataset.shippingEstimatorReady) continue;

      const estimator = wrapper.querySelector('.shipping-estimator');
      if (!estimator) continue;

      wrapper.dataset.shippingEstimatorReady = 'true';

      wrapper.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('.shipping_estimator_submit')) return;

          event.preventDefault();
          event.stopPropagation();
          estimate(estimator);
        },
        true
      );

      wrapper.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || !event.target.classList.contains('shipping-estimator-zip')) return;

        event.preventDefault();
        estimate(estimator);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
